<template>
  <div v-if="stockId">
    <div v-if="!isDashboardAvailable">
      <p class="text-center text-muted">No permission available for inventory dashboard</p>
    </div>
    <div v-if="isDashboardAvailable">
      <div>
        <div class="text-center" v-if="!stockTypeIsGlass">No summary available for {{formatLookupName(stockType, InventoryStockTypeLookup)}} stock types</div>
      </div>

      <div class="row" v-if="stockTypeIsGlass">
        <div class="col-12">
          <InventoryStockSummaryWidget :gridHeight="320" :stockId="stockId" :stockType="stockType" />
        </div>
      </div>

      <div class="row" v-if="stockTypeIsGlass">
        <div class="col-12">
          <PurchasingIncomingStockWidget :gridHeight="300" :stockId="stockId" :endDate="incomingFilterEndDate" />
        </div>
      </div>

      <div class="row" v-if="stockTypeIsGlass">
        <div class="col-12">
          <InventoryGlassSizeBreakdownWidget :stockId="stockId" />
        </div>
      </div>

      <div class="row" v-if="stockTypeIsGlass">
        <div class="col-12">
          <InventoryGlassSizeProjectionWidget :stockId="stockId" />
        </div>
      </div>

      <div class="row" v-if="stockTypeIsGlass">
        <div class="col-12">
          <InventoryStockSalesBreakdownWidget :stockId="stockId" />
        </div>
      </div>

      <div class="row" v-if="stockTypeIsGlass">
        <div class="col-12">
          <DashboardTabbedContentBox :gridHeight="680" large>
            <template v-slot:tablist>
              <Tab :first="true" tabid="tab-sales-contact">
                <i class="tabbedbox-tab-icon fas fa-user"></i>
                <div class="tabbedbox-tab-info">
                  <div class="tabbedbox-tab-title">Contact</div>
                  <div class="tabbedbox-tab-subtitle">Order Item Value Chart</div>
                </div>
              </Tab>
              <Tab tabid="tab-sales-contact-units">
                <i class="tabbedbox-tab-icon fas fa-user"></i>
                <div class="tabbedbox-tab-info">
                  <div class="tabbedbox-tab-title">Contact</div>
                  <div class="tabbedbox-tab-subtitle">Order Item Square Metres Chart</div>
                </div>
              </Tab>
            </template>
            <template v-slot:commoncontent>
              <div class="dashboard-filter-container">
                <div class="row no-gutters">
                  <div class="col-6">
                    <ReportFilterPopover
                      headerTitle="Order Item Value Filter"
                      :showApplyButton="true"
                      :showResetButton="true"
                      :appendToBody="true"
                      alignment="left"
                      placement="bottom-end"
                      @apply="applyOrderItemValueFilter"
                      @reset="resetOrderItemValueFilter">
                      <template v-slot:popover>
                        <ReportDateRangeSelect 
                          :startDate="editingOrderItemValueFilter.startDate"
                          :endDate="editingOrderItemValueFilter.endDate"
                          @updateStartDate="editingOrderItemValueFilter.startDate = $event"
                          @updateEndDate="editingOrderItemValueFilter.endDate = $event"
                        />
                      </template>
                      <template v-slot:filterDisplay>
                        <div>
                          <i class="far fa-calendar mr-2"></i>{{appliedOrderItemValueFilter.startDate | formatDate}} - {{appliedOrderItemValueFilter.endDate | formatDate}}
                        </div>                
                      </template>
                    </ReportFilterPopover>
                  </div>
                </div>
              </div>
            </template>
            <template v-slot:tabcontent>
              <TabPage :first="true" tabid="tab-sales-contact" class="tabpage-padding-2">
                <OrderItemValueChartWidget
                  :stockId="stockId"
                  :type="OrderItemValueChartTypes.orderItemValueContact"
                  :gridHeight="500"
                  :embedded="true"
                  :showList="true"
                  :dateRangeType="ChartDateRangeTypes.custom"
                  :startDate="appliedOrderItemValueFilter.startDate"
                  :endDate="appliedOrderItemValueFilter.endDate"
                  :showDateFilter="false"
                  @updateEditingStartDate="editingOrderItemValueFilter.startDate = $event"
                  @updateEditingEndDate="editingOrderItemValueFilter.endDate = $event"
                  @dateApplied="applyOrderItemValueFilter"
                  @dateReset="resetOrderItemValueFilter"
                />
              </TabPage>
              <TabPage tabid="tab-sales-contact-units" class="tabpage-padding-2">
                <OrderItemValueChartWidget
                  :stockId="stockId"
                  :type="OrderItemValueChartTypes.orderItemUnitsContact"
                  :gridHeight="500"
                  :embedded="true"
                  :showList="true"
                  :dateRangeType="ChartDateRangeTypes.custom"
                  :startDate="appliedOrderItemValueFilter.startDate"
                  :endDate="appliedOrderItemValueFilter.endDate"
                  :showDateFilter="false"
                  @updateEditingStartDate="editingOrderItemValueFilter.startDate = $event"
                  @updateEditingEndDate="editingOrderItemValueFilter.endDate = $event"
                  @dateApplied="applyOrderItemValueFilter"
                  @dateReset="resetOrderItemValueFilter"
                />
              </TabPage>
              
            </template>
          </DashboardTabbedContentBox>
        </div>
      </div>

      <div class="row" v-if="stockTypeIsGlass">
        <div class="col-12">
          <DashboardTabbedContentBox :gridHeight="630" large>
            <template v-slot:tablist>
              <Tab :first="true" tabid="tab-stocklevel-daily">
                <i class="tabbedbox-tab-icon fas fa-calendar-day"></i>
                <div class="tabbedbox-tab-info">
                  <div class="tabbedbox-tab-title">Daily</div>
                  <div class="tabbedbox-tab-subtitle">Stock Levels &amp; Projections</div>
                </div>
              </Tab>
              <Tab tabid="tab-stocksize-stockdailyusage">
                <i class="tabbedbox-tab-icon fas fa-chart-line"></i>
                <div class="tabbedbox-tab-info">
                  <div class="tabbedbox-tab-title">Daily</div>
                  <div class="tabbedbox-tab-subtitle">Stock Usage</div>
                </div>
              </Tab>
              <Tab tabid="tab-stocksize-stockmonthlyusage">
                <i class="tabbedbox-tab-icon fas fa-chart-line"></i>
                <div class="tabbedbox-tab-info">
                  <div class="tabbedbox-tab-title">Monthly</div>
                  <div class="tabbedbox-tab-subtitle">Stock Usage</div>
                </div>
              </Tab>
              <Tab tabid="tab-stocksize-movingaverage">
                <i class="tabbedbox-tab-icon fas fa-chart-line"></i>
                <div class="tabbedbox-tab-info">
                  <div class="tabbedbox-tab-title">Moving Average</div>
                  <div class="tabbedbox-tab-subtitle">Moving Average Over Time</div>
                </div>
              </Tab>
              <Tab tabid="tab-stock-stocktransactions">
                <i class="tabbedbox-tab-icon fas fa-chart-bar"></i>
                <div class="tabbedbox-tab-info">
                  <div class="tabbedbox-tab-title">Daily</div>
                  <div class="tabbedbox-tab-subtitle">Stock Transactions</div>
                </div>
              </Tab>
              <Tab tabid="tab-stock-stockordervalue">
                <i class="tabbedbox-tab-icon fas fa-chart-bar"></i>
                <div class="tabbedbox-tab-info">
                  <div class="tabbedbox-tab-title">Daily</div>
                  <div class="tabbedbox-tab-subtitle">Order Value</div>
                </div>
              </Tab>
            </template>
            <template v-slot:tabcontent>
              <TabPage :first="true" tabid="tab-stocklevel-daily" class="tabpage-padding-2">
                <InventoryStockLevelChartWidget :gridHeight="500" :embedded="true" :stockId="stockId" :stockFilterEnabled="false" />
              </TabPage>
              <TabPage tabid="tab-stocksize-stockdailyusage" class="tabpage-padding-2">
                <InventoryStockDailyUsageChartWidget :gridHeight="500" :embedded="true" :stockId="stockId" :stockFilterEnabled="false" />
              </TabPage>
              <TabPage tabid="tab-stocksize-stockmonthlyusage" class="tabpage-padding-2">
                <InventoryStockMonthlyUsageChartWidget :gridHeight="500" :embedded="true" :stockId="stockId" :stockFilterEnabled="false" />
              </TabPage>
              <TabPage tabid="tab-stocksize-movingaverage" class="tabpage-padding-2">
                <InventoryStockMovingAverageChartWidget :gridHeight="500" :embedded="true" :stockId="stockId" :stockFilterEnabled="false" />
              </TabPage>
              <TabPage tabid="tab-stock-stocktransactions" class="tabpage-padding-2">
                <InventoryStockTransactionChartWidget :gridHeight="500" :embedded="true" :stockId="stockId" :stockFilterEnabled="false" />
              </TabPage>
              <TabPage tabid="tab-stock-stockordervalue" class="tabpage-padding-2">
                <OrderItemDailyChartWidget :gridHeight="500" :embedded="true" :itemId="stockId" />
              </TabPage>
            </template>
          </DashboardTabbedContentBox>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import DashboardContentBox from '@/components/dashboard/dashboardContentBox'
import DashboardTabbedContentBox from '@/components/dashboard/dashboardTabbedContentBox'
import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'

import InventoryStockSummaryWidget from '@/modules/intelligence/dashboardWidgets/inventoryStockSummaryWidget'
import InventoryGlassSizeBreakdownWidget from '@/modules/intelligence/dashboardWidgets/inventoryGlassSizeBreakdownWidget'
import InventoryGlassSizeProjectionWidget from '@/modules/intelligence/dashboardWidgets/inventoryGlassSizeProjectionWidget'
import InventoryStockSalesBreakdownWidget from '@/modules/intelligence/dashboardWidgets/inventoryStockSalesBreakdownWidget'
import InventoryStockLevelChartWidget from '@/modules/intelligence/dashboardWidgets/inventoryStockLevelChartWidget'
import InventoryStockMovingAverageChartWidget from '@/modules/intelligence/dashboardWidgets/inventoryStockMovingAverageChartWidget'
import InventoryStockTransactionChartWidget from '@/modules/intelligence/dashboardWidgets/inventoryStockTransactionChartWidget'
import PurchasingIncomingStockWidget from '@/modules/intelligence/dashboardWidgets/purchasingIncomingStockWidget'
import OrderItemDailyChartWidget from '@/modules/intelligence/dashboardWidgets/orderItemDailyChartWidget'
import OrderItemValueChartWidget from '@/modules/intelligence/dashboardWidgets/orderItemValueChartWidget'
import InventoryStockDailyUsageChartWidget from '@/modules/intelligence/dashboardWidgets/inventoryStockDailyUsageChartWidget'
import InventoryStockMonthlyUsageChartWidget from '@/modules/intelligence/dashboardWidgets/inventoryStockMonthlyUsageChartWidget'

import { InventoryStockTypes, InventoryStockTypeLookup } from '@/store/types'
import { ChartDateRangeTypes } from '@/modules/intelligence/types'

export default {
  name: 'AnalyticsStockItemDashboard',
  components: {
    DashboardContentBox,
    DashboardTabbedContentBox,
    AnalyticsWidget,
    ReportFilterPopover,
    ReportDateRangeSelect,

    InventoryStockSummaryWidget,
    InventoryGlassSizeBreakdownWidget,
    InventoryGlassSizeProjectionWidget,
    InventoryStockSalesBreakdownWidget,
    InventoryStockLevelChartWidget,
    InventoryStockMovingAverageChartWidget,
    InventoryStockTransactionChartWidget,
    InventoryStockDailyUsageChartWidget,
    InventoryStockMonthlyUsageChartWidget,
    PurchasingIncomingStockWidget,
    OrderItemDailyChartWidget,
    OrderItemValueChartWidget,
  },
  props: {
    stockId: Number,
    stockType: String
  },
  data() {
    return {
      incomingFilterEndDate: moment().local().add(6, 'months').endOf('month').endOf('day').toDate().toISOString(),

      defaultOrderItemValueFilter: {    
        startDate: moment().local().subtract(3, 'months').startOf('month').toDate().toISOString(),
        endDate: moment().local().endOf('day').toDate().toISOString()
      },
      editingOrderItemValueFilter: {},
      appliedOrderItemValueFilter: {},
    }
  },
  computed: {
    isDashboardAvailable() {
      if(this.userHasReadPermission(this.PermissionTypes.IntelligencePermissionTypes.intelligenceInventory) || this.userHasReadPermission(this.PermissionTypes.IntelligencePermissionTypes.intelligenceInventory))
        return true
      return false
    },
    stockTypeIsGlass() { return this.stockType == InventoryStockTypes.glass },
    stockTypeIsGlassService() { return this.stockType == InventoryStockTypes.glassService },
    stockTypeIsTemplate() { return this.stockType == InventoryStockTypes.template },
    stockTypeIsAssembly() { return this.stockType == InventoryStockTypes.assembly },
    stockTypeIsGeneral() { return this.stockType == InventoryStockTypes.general },
  },
  created() {
    this.InventoryStockTypeLookup = InventoryStockTypeLookup
    this.OrderItemValueChartTypes = OrderItemValueChartWidget.OrderItemValueChartTypes
    this.ChartDateRangeTypes = ChartDateRangeTypes
    
  },
  mounted() {
    this.editingOrderItemValueFilter = _.clone(this.defaultOrderItemValueFilter)
    this.appliedOrderItemValueFilter = _.clone(this.defaultOrderItemValueFilter)
  },
  methods: {
    applyOrderItemValueFilter() {
      this.appliedOrderItemValueFilter = _.clone(this.editingOrderItemValueFilter)
    },
    resetOrderItemValueFilter() {
      this.editingOrderItemValueFilter = _.clone(this.defaultOrderItemValueFilter)
      this.appliedOrderItemValueFilter = _.clone(this.defaultOrderItemValueFilter)
    },
  }
}
</script>